import { useQuery } from '@tanstack/react-query';
import { useDispatch } from 'react-redux';

import { type PublicClip, ShortUrl } from '@air/api';
import { type Clip } from '@air/api/types';
import { useShortIdContext } from '@air/provider-short-id';

import { setCentralizedClipAction } from '~/store/centralizedClip/actions';
import { type CentralizedClip } from '~/store/centralizedClip/types';

type UsePublicAssetClipParams = {
  clipId: Clip['id'];
};

const PUBLIC_ASSET_CLIP = 'PUBLIC_ASSET_CLIP';

export const getPublicAssetClipKey = ({ clipId, shortId }: { clipId: Clip['id']; shortId: string }) => [
  PUBLIC_ASSET_CLIP,
  { clipId, shortId },
];

/**
 * This hook loads in the current clip/version for the public asset.
 * It takes a clipId so that it can load in the different versions if the user
 * changes versions in the modal
 */
export function usePublicAssetClip({
  clipId,
  initialData,
}: UsePublicAssetClipParams & {
  initialData: PublicClip | undefined;
}) {
  const dispatch = useDispatch();
  const { shortId } = useShortIdContext();

  return useQuery({
    queryKey: getPublicAssetClipKey({ clipId, shortId }),

    queryFn: async () => {
      const { clip } = await ShortUrl.getClip({ clipId, shortId });

      let clipWithContext: CentralizedClip;
      if (initialData && 'boardContext' in initialData && typeof initialData.boardContext === 'string') {
        clipWithContext = { ...clip, boards: [], boardContext: initialData.boardContext };
      } else {
        clipWithContext = { ...clip, boards: [] };
      }

      dispatch(setCentralizedClipAction({ clip: clipWithContext }));

      return clipWithContext;
    },
    enabled: !!clipId,
    staleTime: 0,
    initialData,
  });
}
